import { useMemo, useState } from "react";
import Avatar from "../components/Avatar";
import Button from "../components/Button";
import Icon from "../components/icons/Icon";
import PersonenListe from "../components/PersonenListe";
import QrKarte from "../components/QrKarte";
import SpielMenue from "../components/SpielMenue";
import StatusChip from "../components/StatusChip";
import { WartePunkte } from "../components/StatusPille";
import TextFeld from "../components/TextFeld";
import { farbNummern } from "../spieler";
import "./LobbyScreen.css";

const leuteText = (n) => (n === 1 ? "1 Person" : `${n} Leute`);

/** Warteraum vor dem Spiel. Host: Code zeigen, Leute rauswerfen, starten. */
export default function LobbyScreen({ room, hostPlayerName, setHostPlayerName, onStart, onVerlassen }) {
  const [menue, setMenue] = useState(false);
  const [entwurf, setEntwurf] = useState("");
  const isHost = room.role === "host";
  const players = room.room?.players || [];
  const code = room.room?.code || room.session?.code || "";
  const namenSchluessel = players.map((p) => p.name).join("\n");
  const farbe = useMemo(() => farbNummern(namenSchluessel.split("\n")), [namenSchluessel]);
  const ich = players.find((p) => p.id === room.myPlayerId);
  const hostSpieltMit = players.some((p) => p.isHost);
  const hostName = hostPlayerName.trim();

  const personen = players.map((p) => ({
    key: p.id,
    name: p.name,
    online: p.connected,
    ich: p.id === room.myPlayerId,
    host: !!p.isHost,
  }));

  const verlassenText = isHost ? "Lobby schließen" : "Raum verlassen";
  const menueSheet = menue && (
    <SpielMenue
      sofortFragen={menue === "fragen"}
      onClose={() => setMenue(false)}
      eintraege={[
        {
          icon: "tuer",
          label: verlassenText,
          gefahr: true,
          frage: isHost
            ? "Du gehst zurück ins Setup. Der Raum bleibt noch eine Weile offen, die Mitspieler warten dort."
            : "Du verlässt den Raum. Mit dem Code kommst du wieder rein.",
          bestaetigen: verlassenText,
          onClick: onVerlassen,
        },
      ]}
    />
  );

  if (!isHost) {
    return (
      <div className="spiel lobby">
        <header className="spiel-kopf">
          <div className="spiel-kopf-runde">
            <small>Raum</small>
            <strong>{code}</strong>
          </div>
          <StatusChip status={room.status} />
          <button type="button" className="ikon-knopf" onClick={() => setMenue(true)} aria-label="Menü" title="Menü">
            <Icon name="menue" />
          </button>
        </header>
        <main className="spiel-mitte">
          <div className="ansage">
            {ich && <Avatar name={ich.name} farbe={farbe(ich.name)} groesse="riesig" puls />}
            <h1>Du bist drin!</h1>
            <p>
              Gleich geht's los – der Host startet das Spiel. <WartePunkte />
            </p>
          </div>
          <section className="abschnitt">
            <div className="abschnitt-titel">
              <h2>Dabei</h2>
              <span>{leuteText(players.length)}</span>
            </div>
            <PersonenListe personen={personen} farbe={farbe} />
          </section>
        </main>
        <div className="daumenzone">
          <Button variant="text" onClick={() => setMenue("fragen")}>
            <Icon name="tuer" />
            Raum verlassen
          </Button>
        </div>
        {menueSheet}
      </div>
    );
  }

  return (
    <div className="spiel lobby">
      <header className="spiel-kopf">
        <div className="spiel-kopf-runde">
          <small>Lobby</small>
          <strong>Buzzer-Modus</strong>
        </div>
        <StatusChip status={room.status} text={room.status === "connected" ? "Raum offen" : undefined} />
        <button type="button" className="ikon-knopf" onClick={() => setMenue(true)} aria-label="Menü" title="Menü">
          <Icon name="menue" />
        </button>
      </header>

      <main className="spiel-mitte oben">
        <QrKarte code={code} />

        {/* Rueckfall: kein Name aus dem Setup (alter Stand). Der Knopf setzt
            nur den Namen — beitreten tut App.js automatisch, einmal. */}
        {isHost && !hostSpieltMit && !hostName && (
          <section className="abschnitt">
            <h2>Wie heißt du?</h2>
            <div className="mitspielen-zeile">
              <TextFeld
                className="klein"
                value={entwurf}
                onChange={(e) => setEntwurf(e.target.value.slice(0, 20))}
                placeholder="Dein Name"
                maxLength={20}
                aria-label="Dein Name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && entwurf.trim()) setHostPlayerName(entwurf.trim());
                }}
              />
              <Button variant="neben" disabled={!entwurf.trim()} onClick={() => setHostPlayerName(entwurf.trim())}>
                <Icon name="plus" />
                Mitspielen
              </Button>
            </div>
          </section>
        )}

        <section className="abschnitt">
          <div className="abschnitt-titel">
            <h2>Dabei</h2>
            <span>{leuteText(players.length)}</span>
          </div>
          <PersonenListe
            personen={personen}
            farbe={farbe}
            // Kein X am eigenen Platz: der Rauswurf kappt die Verbindung,
            // beim Host waere das seine eigene.
            onEntfernen={(id) => room.kickPlayer(id)}
            leerText="Noch niemand da – zeig den Code oder teil den Link."
          />
        </section>
      </main>

      <div className="daumenzone klebt">
        <Button variant="haupt" disabled={players.length === 0} onClick={() => onStart(players)}>
          <Icon name="play" />
          {players.length === 0 ? "Warte auf Mitspieler" : "Spiel starten"}
        </Button>
        <Button variant="text" onClick={() => setMenue("fragen")}>
          Lobby schließen
        </Button>
      </div>
      {menueSheet}
    </div>
  );
}
